import {Injectable} from '@angular/core';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {catchError, switchMap} from 'rxjs/operators';
import {from, of} from 'rxjs';
import {LoadError} from '@app/core/store/actions/error.actions';
import {
	EDepartmentSettingsActions,
	GetDepartment,
	GetDepartmentSuccess,
	GetIntegrations,
	GetIntegrationsSuccess,
	GetRoundingRules,
	GetRoundingRulesSuccess,
	GetTenant,
	GetTenantSuccess,
	PatchDepartment,
	PatchDepartmentSuccess,
	PatchTenant,
	PatchTenantSuccess,
} from '@app/core/store/actions/department-settings.actions';
import {DepartmentService as OldDepartmentService} from '@app/modules/shared/services/department.service';
import {Department} from '@app/core/models/department.model';
import {TenantService} from '@app/modules/shared/services/tenant.service';
import {Tenant} from '@app/core/models/tenant.model';
import {QMIntegration} from '@app/core/models/QMIntegration.model';
import {IntegrationService} from '@app/core/services/api/integration.service';
import {DepartmentService} from '@app/core/services/api/department.service';
import {RoundingRule} from '@app/core/models/roundingRule.model';
import {SnackBar} from '@app/core/services/snackbar.service';
import {EQOLogLevel, QOLogService} from '@quickorder/angular-web-lib';
import {TranslateService} from '@ngx-translate/core';

@Injectable()
export class DepartmentSettingsEffects {

	@Effect()
	public onGetDepartmentSettings$ = this.actions$.pipe(
		ofType<GetDepartment>(EDepartmentSettingsActions.GetDepartment),
		switchMap((action: GetDepartment) => from(this.oldDepartmentService.get(action.id)).pipe(
			switchMap((department: Department) => [
				new GetDepartmentSuccess(department),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onPatchDepartment$ = this.actions$.pipe(
		ofType<PatchDepartment>(EDepartmentSettingsActions.PatchDepartment),
		switchMap((action: PatchDepartment) => from(this.oldDepartmentService.patch(action.id, action.payload)).pipe(
			switchMap((department: Department) => {

				if (action.payload.email !== department.email && action.emailVerificationPopup) {
					this.logService.logContent({
						level: EQOLogLevel.INFO,
						title: this.translate.instant('SNACKBAR.DONE'),
						description: this.translate.instant('EMAIL_VERIFICATION.EMAIL_VERIFICATION_REQUIRED'),
						forceDuration: 999999,
						actions: [],
					});
				} else {
					this.snackBar.newSuccess('DEPARTMENT.UPDATED');
				}

				return [
					new PatchDepartmentSuccess(department),
				];
			}),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onGetTenantSettings$ = this.actions$.pipe(
		ofType<GetTenant>(EDepartmentSettingsActions.GetTenant),
		switchMap((action: GetTenant) => from(this.tenantService.get()).pipe(
			switchMap((tenant: Tenant) => [
				new GetTenantSuccess(tenant),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);
	@Effect()
	public onGetIntegrationsSettings$ = this.actions$.pipe(
		ofType<GetIntegrations>(EDepartmentSettingsActions.GetIntegrations),
		switchMap((action: GetIntegrations) => from(this.integrationService.getIntegrations().pipe(
			switchMap((integrations: QMIntegration[]) => [
				new GetIntegrationsSuccess(integrations),
			]),
			catchError(error => of(new LoadError(error, action))),
			)),
		));

	@Effect()
	public onPatchTenant$ = this.actions$.pipe(
		ofType<PatchTenant>(EDepartmentSettingsActions.PatchTenant),
		switchMap((action: PatchTenant) => from(this.tenantService.patch(action.payload)).pipe(
			switchMap((tenant: Tenant) => {
				if (action.payload.email !== tenant.email && action.emailVerificationPopup) {
					this.logService.logContent({
						level: EQOLogLevel.INFO,
						title: this.translate.instant('SNACKBAR.DONE'),
						description: this.translate.instant('EMAIL_VERIFICATION.EMAIL_VERIFICATION_REQUIRED'),
						forceDuration: 999999,
						actions: [],
					});
				} else {
					this.snackBar.newSuccess();
				}

				return [
					new PatchTenantSuccess(tenant),
				];
			}),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onGetRoundingRules = this.actions$.pipe(
		ofType<GetRoundingRules>(EDepartmentSettingsActions.GetRoundingRules),
		switchMap((action: GetRoundingRules) => from(this.departmentService.getRoundingRules({
			'filter[department_id]': action.departmentId,
		})).pipe(
			switchMap((roundingRules: RoundingRule[]) => [
				new GetRoundingRulesSuccess(roundingRules),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	constructor(
		private actions$: Actions,
		private oldDepartmentService: OldDepartmentService,
		private departmentService: DepartmentService,
		private tenantService: TenantService,
		private integrationService: IntegrationService,
		private snackBar: SnackBar,
		private logService: QOLogService,
		private translate: TranslateService,
	) {
	}
}
